/* eslint-disable no-unused-vars */
// src/pages/KakaoCallback.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../store/authStore";
import userHooks from "../hooks/userHooks";

const KakaoCallback = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 성공 시 리다이렉트 처리
  const handleSuccessRedirect = useCallback(
    (path) => {
      const savedPath = localStorage.getItem("kakao_login_redirect");
      localStorage.removeItem("kakao_login_redirect");

      navigate(savedPath || path, { replace: true });

      setTimeout(() => {
        const currentLength = window.history.length;
        if (currentLength > 2) {
          window.history.go(-(currentLength - 3));
        }
      }, 100);
    },
    [navigate]
  );

  useEffect(() => {
    const handleKakaoCallback = async () => {
      try {
        // URL에서 인가 코드 가져오기
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        const error = urlParams.get("error");
        const state = urlParams.get("state");

        // 에러가 있는 경우
        if (error) {
          console.error("카카오 로그인 에러:", error);
          throw new Error("카카오 로그인이 취소되었거나 실패했습니다.");
        }

        if (!code) {
          throw new Error("인가 코드를 받지 못했습니다.");
        }

        if (!window.Kakao || !window.Kakao.isInitialized()) {
          await loadKakaoSDK();
        }

        const tokenResponse = await fetch(
          "https://kauth.kakao.com/oauth/token",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams({
              grant_type: "authorization_code",
              client_id: process.env.REACT_APP_KAKAO_JAVASCRIPT_KEY,
              code: code,
              redirect_uri: `${window.location.origin}/kakao/callback`,
            }),
          }
        );

        if (!tokenResponse.ok) {
          throw new Error("토큰 요청 실패");
        }

        const tokenData = await tokenResponse.json();
        console.log("토큰 수신 성공");

        window.Kakao.Auth.setAccessToken(tokenData.access_token);

        const userResponse = await window.Kakao.API.request({
          url: "/v2/user/me",
        });

        const kakaoAccount = userResponse.kakao_account;
        if (!kakaoAccount) {
          throw new Error("카카오 계정 정보를 가져올 수 없습니다.");
        }

        // 로그인 시도
        const loginResponse = await userHooks.signinHooks({
          private_id: `${userResponse.id}`,
        });

        if (loginResponse.status) {
          // 기존 회원 로그인 성공
          const authStore = useAuthStore.getState();
          authStore.setAuth(loginResponse);

          console.log("로그인 성공:", loginResponse);

          if (loginResponse.isSignedUp && loginResponse.baby.length > 0) {
            // 아기 정보가 있는 완전한 회원
            handleSuccessRedirect("/");
          } else {
            // 아기 정보 입력이 필요한 회원
            handleSuccessRedirect("/authentication");
          }
        } else {
          // 신규 회원 - 회원가입 진행
          console.log("신규 회원 - 회원가입 진행");

          const userData = {
            private_id: `${userResponse.id}`,
            user_name: kakaoAccount.profile?.nickname || "",
            message_allow: false,
            gender: kakaoAccount.gender === "female" ? "0" : "1",
            hp: "",
            private_consent: true,
            marketing_consent: true,
            birth: kakaoAccount.birthday || "",
          };

          const signupResponse = await userHooks.signupHooks(userData);

          if (signupResponse.status) {
            const authStore = useAuthStore.getState();
            authStore.setAuth(signupResponse);
            console.log("회원가입 성공");
            handleSuccessRedirect("/authentication");
          } else {
            if (signupResponse.message === "duplicated") {
              // 중복 회원 - 재로그인 시도
              console.log("이미 가입된 회원입니다. 로그인을 재시도합니다.");

              const retryLoginResponse = await userHooks.signinHooks({
                private_id: `${userResponse.id}`,
              });

              if (retryLoginResponse.status) {
                const authStore = useAuthStore.getState();
                authStore.setAuth(retryLoginResponse);

                if (
                  retryLoginResponse.isSignedUp &&
                  retryLoginResponse.baby.length > 0
                ) {
                  handleSuccessRedirect("/");
                } else {
                  handleSuccessRedirect("/authentication");
                }
              } else {
                throw new Error("로그인에 실패했습니다.");
              }
            } else {
              throw new Error(
                signupResponse.message || "회원가입에 실패했습니다."
              );
            }
          }
        }
      } catch (error) {
        console.error("카카오 콜백 처리 실패:", error);
        setError(error.message);

        // 3초 후 메인 페이지로 이동
        setTimeout(() => {
          navigate("/", { replace: true });
        }, 3000);
      } finally {
        setIsLoading(false);
      }
    };

    handleKakaoCallback();
  }, [navigate, handleSuccessRedirect]);

  // 카카오 SDK 로드 함수
  const loadKakaoSDK = () => {
    return new Promise((resolve, reject) => {
      if (window.Kakao && window.Kakao.isInitialized()) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://developers.kakao.com/sdk/js/kakao.js";
      script.async = true;
      script.onload = () => {
        const KAKAO_KEY = process.env.REACT_APP_KAKAO_JAVASCRIPT_KEY;
        if (!window.Kakao.isInitialized()) {
          window.Kakao.init(KAKAO_KEY);
        }
        resolve();
      };
      script.onerror = () => {
        reject(new Error("카카오 SDK 로드 실패"));
      };
      document.head.appendChild(script);
    });
  };

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-white px-4">
        <div className="text-center">
          <div className="text-lg font-semibold text-red-500 mb-4">
            로그인 처리 중 오류가 발생했습니다
          </div>
          <div className="text-sm text-gray-600 mb-6">{error}</div>
          <div className="text-sm text-gray-400">
            잠시 후 메인 페이지로 이동합니다...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center h-screen bg-white">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
        <div className="text-lg font-semibold text-gray-700 mb-2">
          로그인 처리 중입니다
        </div>
        <div className="text-sm text-gray-500">잠시만 기다려주세요...</div>
      </div>
    </div>
  );
};

export default KakaoCallback;
